import { useEffect, useState } from 'react';

import { isEmpty, isNonNullish } from 'remeda';
import { SettingsContainer } from 'wbs-components-lib';

import { Typography } from '@mui/material';

import { RewardModel, RewardModelWithChance, RewardModelWithoutId } from '@shared/types';
import { transformRewardsFromObjectToMap } from '@shared/utils';

import {
  useBulkDeleteRewardsMutation,
  useCreateRewardMutation,
  useGetVipWheelSettingsQuery,
  useLazyGetRewardsByIdsQuery,
  useUpdateVipWheelSettingsMutation,
  useUpdateRewardMutation,
} from '@services';

import { VIP_WHEEL_SETTINGS_DEFAULT_VALUES } from './constants';
import { getEditFormInitialValues, updateVipWheelRewards } from './utils';
import { VipWheelSettingsForm, VipWheelSettingsFormValues } from './VipWheelSettingsForm';

export const VipWheelSettingsTab = () => {
  const [initialDefaultRewards, setInitialDefaultRewards] = useState<RewardModelWithChance[]>([]);
  const [initialDiamondRewards, setInitialDiamondRewards] = useState<RewardModelWithChance[]>([]);
  const [initialEliteRewards, setInitialEliteRewards] = useState<RewardModelWithChance[]>([]);

  const { data: vipWheelSettings } = useGetVipWheelSettingsQuery();

  const [updateVipWheelSettings, { isLoading: isVipWheelSettingsUpdating }] = useUpdateVipWheelSettingsMutation();

  const [getRewardsByIds] = useLazyGetRewardsByIdsQuery();
  const [bulkDeleteRewards] = useBulkDeleteRewardsMutation();
  const [createReward] = useCreateRewardMutation();
  const [updateReward] = useUpdateRewardMutation();

  useEffect(() => {
    if (vipWheelSettings === undefined) {
      return;
    }

    const { defaultRewardsMap, diamondRewardsMap, eliteRewardsMap } = vipWheelSettings;

    const getInitialRewards = async () => {
      const currentDefaultRewards = await getRewardsByIds(Object.keys(defaultRewardsMap)).unwrap();
      const currentDiamondRewards = await getRewardsByIds(Object.keys(diamondRewardsMap)).unwrap();
      const currentEliteRewards = await getRewardsByIds(Object.keys(eliteRewardsMap)).unwrap();

      setInitialDefaultRewards(transformRewardsFromObjectToMap(defaultRewardsMap, currentDefaultRewards));
      setInitialDiamondRewards(transformRewardsFromObjectToMap(diamondRewardsMap, currentDiamondRewards));
      setInitialEliteRewards(transformRewardsFromObjectToMap(eliteRewardsMap, currentEliteRewards));
    };

    getInitialRewards();
  }, [vipWheelSettings]);

  const handleUpdatingVipWheelSettings = async (formValues: VipWheelSettingsFormValues) => {
    const {
      defaultRewardsMap,
      diamondRewardsMap,
      eliteRewardsMap,
      defaultCryptocheckLinks,
      diamondCryptocheckLinks,
      eliteCryptocheckLinks,
      ...settingsData
    } = formValues;

    const createRewardCallback = async (reward: RewardModel) => createReward(reward).unwrap();
    const updateRewardCallback = async (payload: { rewardId: string; payload: RewardModelWithoutId }) =>
      updateReward(payload).unwrap();
    const bulkDeleteRewardsCallback = async (rewardIds: string[]) => bulkDeleteRewards(rewardIds);

    await updateVipWheelSettings({
      ...settingsData,
      defaultCryptocheckLinks: defaultCryptocheckLinks.map((item) => item.value) as string[],
      diamondCryptocheckLinks: diamondCryptocheckLinks.map((item) => item.value) as string[],
      eliteCryptocheckLinks: eliteCryptocheckLinks.map((item) => item.value) as string[],
      defaultRewardsMap: await updateVipWheelRewards(
        initialDefaultRewards,
        defaultRewardsMap,
        createRewardCallback,
        updateRewardCallback,
        bulkDeleteRewardsCallback
      ),
      diamondRewardsMap: await updateVipWheelRewards(
        initialDiamondRewards,
        diamondRewardsMap,
        createRewardCallback,
        updateRewardCallback,
        bulkDeleteRewardsCallback
      ),
      eliteRewardsMap: await updateVipWheelRewards(
        initialEliteRewards,
        eliteRewardsMap,
        createRewardCallback,
        updateRewardCallback,
        bulkDeleteRewardsCallback
      ),
    }).unwrap();
  };
  const initialValues =
    isNonNullish(vipWheelSettings) && !isEmpty(vipWheelSettings)
      ? getEditFormInitialValues(vipWheelSettings!, initialDefaultRewards, initialDiamondRewards, initialEliteRewards)
      : VIP_WHEEL_SETTINGS_DEFAULT_VALUES;

  return (
    <SettingsContainer>
      <Typography margin="10px" fontSize="24px" fontWeight={600}>
        Vip Wheel Settings
      </Typography>
      <VipWheelSettingsForm
        isSubmitting={isVipWheelSettingsUpdating}
        onSubmit={handleUpdatingVipWheelSettings}
        initialValues={initialValues}
      />
    </SettingsContainer>
  );
};
